import type { ReactNode } from "react";
import type { BannerTone } from "../lib/feedback";

type Props = {
  tone: BannerTone;
  /** Required for critical/warning - merchants scan headings first. */
  heading: string;
  children?: ReactNode;
  /** Upsells / tips only (BFS 4.3.6). Never dismiss page errors. */
  dismissible?: boolean;
  onDismiss?: () => void;
};

/**
 * Standardized page-level message. Prefer one banner per page (BFS 4.3.4).
 * Errors stay visible - do not auto-hide.
 */
export function PageBanner({
  tone,
  heading,
  children,
  dismissible,
  onDismiss,
}: Props) {
  const canDismiss = !!dismissible && tone !== "critical";
  return (
    <s-banner
      tone={tone}
      heading={heading}
      dismissible={canDismiss || undefined}
      onDismiss={canDismiss ? onDismiss : undefined}
    >
      {children}
    </s-banner>
  );
}

/** Critical page error - always has a heading. */
export function ErrorBanner({
  heading = "Something went wrong",
  children,
}: {
  heading?: string;
  children?: ReactNode;
}) {
  return (
    <PageBanner tone="critical" heading={heading}>
      {children}
    </PageBanner>
  );
}

/** Soft guidance (not a failure). */
export function InfoBanner({
  heading,
  children,
  dismissible,
  onDismiss,
}: {
  heading: string;
  children?: ReactNode;
  dismissible?: boolean;
  onDismiss?: () => void;
}) {
  return (
    <PageBanner
      tone="info"
      heading={heading}
      dismissible={dismissible}
      onDismiss={onDismiss}
    >
      {children}
    </PageBanner>
  );
}

export function WarningBanner({
  heading,
  children,
}: {
  heading: string;
  children?: ReactNode;
}) {
  return (
    <PageBanner tone="warning" heading={heading}>
      {children}
    </PageBanner>
  );
}
